import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Search, 
  Eye, 
  Trash2, 
  Calendar,
  User,
  BarChart3,
  ExternalLink,
  Shield,
  Activity,
  UserCheck,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import type { LandingPage } from '../../types/builder';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'hitzlink.com';

interface ExtendedLandingPage extends LandingPage {
  userId: string;
  userName: string;
  userEmail: string;
  views: number;
  uniqueVisitors: number;
  uniqueClicks: number;
  conversionRate: string;
  slug?: string;
}

export const PageManagement: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const navigate = useNavigate();
  const [pages, setPages] = useState<ExtendedLandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<ExtendedLandingPage | null>(null);
  const [previewPage, setPreviewPage] = useState<ExtendedLandingPage | null>(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      if (!token) return;
      const response = await fetch(`${API_URL}/admin/pages`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pages');
      }

      const data = await response.json();
      setPages(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to load pages');
      setLoading(false);
    }
  };

  const filteredPages = pages.filter(page => 
    page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    page.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    page.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUniqueVisitors = pages.reduce((sum, p) => sum + (p.uniqueVisitors || 0), 0);
  const totalUniqueClicks = pages.reduce((sum, p) => sum + (p.uniqueClicks || 0), 0);
  const stats = {
    total: pages.length,
    totalViews: pages.reduce((sum, p) => sum + p.views, 0),
    totalUniqueVisitors,
    totalUniqueClicks,
    avgConvRate: totalUniqueVisitors > 0 ? ((totalUniqueClicks / totalUniqueVisitors) * 100).toFixed(1) : '0.0',
    withCloaking: pages.filter(p => p.settings.cloaking?.enabled).length,
  };

  const handleDeleteClick = (page: ExtendedLandingPage) => {
    setPageToDelete(page);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!pageToDelete) return;

    try {
      if (!token) return;
      const response = await fetch(`${API_URL}/admin/pages/${pageToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete page');
      }

      setPages(pages.filter(p => p.id !== pageToDelete.id));
      toast.success(`Page "${pageToDelete.name}" deleted successfully!`);
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }

    setShowDeleteModal(false);
    setPageToDelete(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPageUrl = (page: ExtendedLandingPage) => {
    // Use subdomain structure (*.hitzlink.com) as per DNS configuration
    const subdomain = page.slug || page.id;
    return `https://${subdomain}.${BASE_DOMAIN}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Activity className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading pages...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-purple-600" />
            Landing Page Management
          </h1>
          <p className="text-gray-600 mt-1">View and manage all landing pages</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pages</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Views</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalViews.toLocaleString()}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-700">Unique Visitors</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalUniqueVisitors.toLocaleString()}</p>
              </div>
              <UserCheck className="w-8 h-8 text-indigo-500" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Unique Clicks</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalUniqueClicks.toLocaleString()}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conv. Rate</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.avgConvRate}%</p>
              </div>
              <BarChart3 className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">With Cloaking</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.withCloaking}</p>
              </div>
              <Shield className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by page name, user name, or email..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Pages Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Page Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Owner</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Views</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Unique Visitors</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Unique Clicks</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Conv. Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Features</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPages.map((page) => (
                  <tr key={page.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm" style={{ backgroundColor: page.settings.backgroundColor || '#6366f1' }}>
                          {page.name.charAt(0)}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{page.name}</div>
                          <div className="text-xs text-gray-500">{page.slug || page.id.substring(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <User className="w-4 h-4" />
                        <div>
                          <div className="font-medium text-gray-900">{page.userName}</div>
                          <div className="text-xs text-gray-500">{page.userEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900">{page.views.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-indigo-600">{(page.uniqueVisitors || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-purple-600">{(page.uniqueClicks || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-bold ${
                        parseFloat(page.conversionRate || '0') >= 5 ? 'text-green-600' :
                        parseFloat(page.conversionRate || '0') >= 2 ? 'text-yellow-600' : 'text-gray-500'
                      }`}>{page.conversionRate || '0.0'}%</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        {page.settings.cloaking?.enabled && (
                          <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs font-semibold">Cloaking</span>
                        )}
                        {page.settings.deviceRedirect?.enabled && (
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-semibold">Device Redirect</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(page.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => window.open(getPageUrl(page), '_blank')}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Open Page"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/cloaking/${page.id}`)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Bot Cloaking · logs & settings"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPreviewPage(page)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Preview Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(page)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Page"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredPages.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No landing pages found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPreviewPage(null)}>
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Details</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Page Name:</span>
                <p className="text-lg font-semibold text-gray-900">{previewPage.name}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">URL:</span>
                <p className="text-sm text-blue-600 break-all">{getPageUrl(previewPage)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Owner:</span>
                <p className="text-gray-900">{previewPage.userName} ({previewPage.userEmail})</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Views:</span>
                <p className="text-gray-900 font-semibold">{previewPage.views.toLocaleString()}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Meta Title:</span>
                <p className="text-gray-900">{previewPage.settings.meta?.title || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Meta Description:</span>
                <p className="text-gray-900">{previewPage.settings.meta?.description || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Components:</span>
                <p className="text-gray-900">{previewPage.components.length} component(s)</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => window.open(getPageUrl(previewPage), '_blank')}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Open Page
              </button>
              <button
                onClick={() => setPreviewPage(null)}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && pageToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the page <span className="font-semibold text-gray-900">"{pageToDelete.name}"</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
